import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { DragulaService } from 'ng2-dragula';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import * as FlatServices from '@medsurf/flat-services';
import * as FlatDesigns from '@medsurf/flat-designs';
import { ModalControlComponent, DragContainerComponent } from '@medsurf/flat-components';
import { PermissionDialogComponent } from '../permission-dialog/permission-dialog.component';
import { PageTitleDialogComponent } from '../page-title-dialog/page-title-dialog.component';
import { PageLinkDialogComponent } from '../page-link-dialog/page-link-dialog.component';

@Component({
    selector: 'medsurf-node-tree',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './node-tree.component.html',
    styleUrls: ['./node-tree.component.scss'],
    standalone: false
})
export class NodeTreeComponent extends DragContainerComponent  {
	@ViewChild('template_node_tree', { static: false }) templateNodeTree: FlatDesigns.TemplateNodeTreeContentDesign;
	
  getNodeMenuItems = (data: FlatModels.ActionMenusAuthorModels.MenuItemsAction) => {
    if (data.type !== "node") return;
    const {routeElement, node, page, parentNode} = data;
    const selectNodeSet = this.bulkEditAuthorFacade.snapshot_selectNodeSet();
    const copyNodeSet = this.bulkEditAuthorFacade.snapshot_copyNodeSet();
    const copyPageSet = this.bulkEditAuthorFacade.snapshot_copyPageSet();
    const isSlide = routeElement.depth === 4;
    return [
      {
        label: this.translate.instant(node.hidden ? 'global.visibility_edit_hidden' : 'global.visibility_edit_visible'),
        icon: (node.hidden ? (FlatModels.ButtonDesignModels.ButtonDefaultIcons.EYE_SLASH + FlatModels.ButtonDesignModels.ButtonDefaultIconsStyle.DANGER) : FlatModels.ButtonDesignModels.ButtonDefaultIcons.EYE),
        command: () => this.#toggleVisibility(node)
      },
      {
        label: this.translate.instant(node.restricted ? 'global.restriction_edit_grant' : 'global.restriction_edit_restrict'),
        visible: routeElement.depth <= 3,
        icon: (node.restricted ? (FlatModels.ButtonDesignModels.ButtonDefaultIcons.LOCK + FlatModels.ButtonDesignModels.ButtonDefaultIconsStyle.DANGER) : FlatModels.ButtonDesignModels.ButtonDefaultIcons.UNLOCK),
        command: () => this.#toggleRestriction(node)
      },
      {
        label: this.translate.instant('global.user_shares_edit'),
        visible: routeElement.depth <= 3,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.KEY,
        command: () => this.#editShares(node, page)
      },
      {
        separator: true,
        visible: isSlide
      },
      {
        label: this.translate.instant('global.node_duplicate'),
        visible: isSlide,
        disabled: true,
        styleClass: 'menu_grouping_label'
      },
      {
        label: this.translate.instant('global.node_duplicate_without_annotations'),
        visible: isSlide,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.DUPLICATE,
        command: () => this.#duplicateNode(node, parentNode, false, false)
      },
      {
        label: this.translate.instant('global.node_duplicate_with_annotations'),
        visible: isSlide,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.DUPLICATE_WITH_ANNOTATIONS,
        command: () => this.#duplicateNode(node, parentNode, false, true)
      },
      {
        label: this.translate.instant('global.node_duplicate_as_training_without_annotations'),
        visible: isSlide,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.DUPLICATE_AS_TRAINING,
        command: () => this.#duplicateNode(node, parentNode, true, false)
      },
      {
        label: this.translate.instant('global.node_duplicate_as_training_with_annotations'),
        visible: isSlide,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.DUPLICATE_AS_TRAINING_WITH_ANNOTATIONS,
        command: () => this.#duplicateNode(node, parentNode, true, true)
      },
      {
        separator: true,
        visible: isSlide,
      },
      {
        label: this.translate.instant('global.node_copy'),
        visible: isSlide,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.COPY,
        command: () => this.#copyNode(node)
      },
      {
        separator: true,
        visible: routeElement.depth === 3
      },
      {
        label: this.translate.instant('global.nodes_copy'),
        visible: routeElement.depth === 3,
        disabled: selectNodeSet.size === 0,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.COPY,
        command: () => this.#copyNodes()
      },
      {
        label: this.translate.instant('global.node_paste'),
        visible: routeElement.depth === 3,
        disabled: copyNodeSet.size === 0,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.#pasteNode(node)
      },
      {
        label: this.translate.instant('global.page_paste'),
        visible: routeElement.depth === 3 && copyPageSet.size !== 0,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.#pastePage(node)
      },
      {
        separator: true,
        visible: isSlide,
      },
      {
        label: `${this.translate.instant('global.pages_link')}`,
        visible: isSlide,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ATTACH,
        command: () => this.#linkPages(page)
      },
      {
        visible: routeElement.depth >= 3 || (routeElement.depth < 3 && node.children.length === 0),
        separator: true
      },
      {
        label: this.translate.instant('global.node_detach'),
        visible: isSlide,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.DETACH,
        command: () => this.#detachNode(node)
      },
      {
        label: this.translate.instant('global.node_remove'),
        visible: routeElement.depth >= 3 || (routeElement.depth < 3 && node.children.length === 0),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.REMOVE,
        styleClass: FlatModels.ButtonDesignModels.ButtonDesignClass.MENU_DANGER,
        command: () => this.#removeNode(node)
      },
    ] satisfies MenuItem[];
  };
  getSlideMenuItems = (data: FlatModels.ActionMenusAuthorModels.MenuItemsAction) => {
    if (data.type !== "slide") return;
    const {routeElement, node} = data;
    return [
      {
        label: this.translate.instant('global.slide_add'),
        disabled: true,
        styleClass: 'menu_grouping_label'
      },
      {
        label: this.translate.instant('global.slide'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_SLIDE,
        command: () => this.#addSlide(routeElement, node)
      },
      {
        label: this.translate.instant('global.training'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_TRAINING,
        command: () => this.#addSlide(routeElement, node, true)
      },
    ] satisfies MenuItem[];
  }
  readonly FlatModels = FlatModels;

  constructor(protected slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              protected appSharedFacade: FlatFacades.AppSharedFacade,
              protected nodeSharedFacade: FlatFacades.NodeSharedFacade,
              protected bulkEditAuthorFacade: FlatFacades.BulkEditAuthorFacade,
              protected historySharedFacade: FlatFacades.HistorySharedFacade,
              protected mediaControlService: FlatServices.MediaControlService,
              private alertControlFacade: FlatFacades.AlertControlFacade,
              private navigationControlFacade: FlatFacades.NavigationControlFacade,
              private dialogService: DialogService,
              private translate: TranslateService,
              dragulaService: DragulaService) {
    super(dragulaService);
  }

  getNodeMenuButtonTitleForTooltip(depth: number) {
    switch (depth) {
      case 0:
        return this.translate.instant('global.subject_add');
      case 1:
        return this.translate.instant('global.chapter_add');
      default:
        return this.translate.instant('global.grid_add');
    }
  }

  //<editor-fold desc="Drag And Drop">

  onNodeMoved(parent: FlatModels.NodeEntityModels.NodeEntityType | null,
              children: FlatModels.NodeEntityModels.NodeEntityType[]) {
    this.nodeSharedFacade.requestRearrangeNodes(parent, children).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }

  //</editor-fold>

  //<editor-fold desc="Node Edit">

  onNodeSelect(event: MouseEvent,
               node: FlatModels.NodeEntityModels.NodeEntityType,
               routeElement: FlatModels.NodeEntityModels.RouteElement) {
    if (this.templateNodeTree.handled) {
      this.templateNodeTree.handled = false;
      return;
    }

    document.getSelection()?.removeAllRanges();
    if (node.children.length > 0) {
      this.nodeSharedFacade.requestNodesByParentId(node.id).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result)
      });
    }
    if (event.ctrlKey || event.metaKey) {
      if (routeElement.depth === 4) {
        return this.bulkEditAuthorFacade.requestToggleNode(node.id, node.parentId, true).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result)
        });
      }
    } else {
      return this.bulkEditAuthorFacade.requestDeselectNodes().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => this.navigationControlFacade.requestNavigate([routeElement.path])
      });
    }
  }

  handleEvent() {
    this.templateNodeTree.handleEvent();
  }

  onNodeMenu(routeElement: FlatModels.NodeEntityModels.RouteElement,
             node: FlatModels.NodeEntityModels.NodeEntityType) {
    this.handleEvent();
  
    if (routeElement.depth !== 4) return;

    // preload page data .. for duplication
    this.slideAuthorFacade.requestGetSlideDataByPageId(node.page).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }

  addNode(routeElement: FlatModels.NodeEntityModels.RouteElement,
          node: FlatModels.NodeEntityModels.NodeEntityType) {
    this.handleEvent();
    const routeElementClone = structuredClone(routeElement);

    const dialogRef = this.dialogService.open(PageTitleDialogComponent, {
      header: this.getNodeMenuButtonTitleForTooltip(routeElement.depth),
      data: {
        inputLabel: this.translate.instant('global.add')
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe((data) => {
      if (data && data.result) {
        // Request Add Node
        this.nodeSharedFacade.requestAddNode(routeElementClone, 'history.node_add', data.title).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => {
            this.templateNodeTree.onNodeExpand(node, true);
            this.templateNodeTree.handled = false;
          }
        });
      }
    });
  }

  #removeNode(node: FlatModels.NodeEntityModels.NodeEntityType) {
    const nodeClone = structuredClone(node);
    
    // Ensure children are loaded
    this.nodeSharedFacade.requestNodesByParentId(node.id).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
    
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.node_remove'),
      data: {
        label: 'global.node_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe((remove) => {
      if (remove) {
        this.nodeSharedFacade.requestRemoveNode(nodeClone, 'history.node_remove').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result)
        });
      }
    });
  }
  
  #addSlide(routeElement: FlatModels.NodeEntityModels.RouteElement,
            node: FlatModels.NodeEntityModels.NodeEntityType,
            isTraining = false) {
    const routeElementClone = structuredClone(routeElement);
    
    const dialogRef = this.dialogService.open(PageTitleDialogComponent, {
      header: this.translate.instant(isTraining ? 'global.training_add_description' : 'global.slide_add_description'),
      data: {
        inputLabel: this.translate.instant('global.add')
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe((data) => {
      if (data?.result) {
        this.nodeSharedFacade.requestAddNode(routeElementClone, 'history.node_add', data.title, isTraining).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => {
            this.templateNodeTree.onNodeExpand(node, true);
            this.templateNodeTree.handled = false;
          }
        });
      }
    });
  }
  
  #copyNode(node: FlatModels.NodeEntityModels.NodeEntityType) {
    this.bulkEditAuthorFacade.requestSetNodes([node.id]).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.bulkEditAuthorFacade.requestCopyNode(node.id).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result)
      })
    });
  }
  
  #copyNodes() {
    this.bulkEditAuthorFacade.requestCopyNodesFromSelectSet().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }

  #duplicateNode(node: FlatModels.NodeEntityModels.NodeEntityType,
                 parentNode: FlatModels.NodeEntityModels.NodeEntityType,
                 asTraining: boolean,
                 withAnnotations: boolean) {
    this.bulkEditAuthorFacade.requestDuplicateNode(node, parentNode, asTraining, withAnnotations).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateNodeTree.onNodeExpand(node, true)
    });
  }
  
  #pasteNode(parentNode: FlatModels.NodeEntityModels.NodeEntityType) {
    this.bulkEditAuthorFacade.requestPasteNodes(parentNode, null, true).subscribe({
      error: (result) => {
        this.alertControlFacade.requestAddError(result);
      },
      next: () => {
        this.templateNodeTree.onNodeExpand(parentNode, true);
      }
    });
  }
  
  #linkPages(page: FlatModels.PageEntityModels.PageEntityType) {
    this.bulkEditAuthorFacade.requestSetPages([page.id]).subscribe(() => {
	    // Show Dialog
	    const dialogRef = this.dialogService.open(PageLinkDialogComponent, {
		    header: this.translate.instant('global.pages_link_title'),
		    dismissableMask: true,
		    width: '70%',
		    height: '90%'
	    });
	    dialogRef.onClose.subscribe((data) => {
		    if (data && data.result) {
			    // Request Attach Pages
			    this.bulkEditAuthorFacade.requestAttachSelectedPagesToNodes(data.nodes).subscribe({
				    error: (result) => this.alertControlFacade.requestAddError(result),
				    next: () => {
					    for(const node of data.nodes) {
						    this.templateNodeTree.onNodeExpand(node, true);
					    }
					    this.alertControlFacade.requestAddAlert({
						    key: 'main',
						    detail: 'notification.slides_linked',
						    severity: FlatModels.AlertControlModels.AlertType.enum.success,
						    life: FlatModels.AlertControlModels.AlertTime.DEFAULT
					    });
				    }
			    });
		    }
	    });
    })
  }
  
  #detachNode(node: FlatModels.NodeEntityModels.NodeEntityType) {
    const nodeClone = structuredClone(node);
    
    this.bulkEditAuthorFacade.requestDetachNode(nodeClone).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }
  
  #pastePage(parentNode: FlatModels.NodeEntityModels.NodeEntityType) {
    const parentNodeClone = structuredClone(parentNode);
    
    this.bulkEditAuthorFacade.requestAttachPages(parentNodeClone).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => this.templateNodeTree.onNodeExpand(parentNodeClone, true)
    });
  }

  #toggleVisibility(node: FlatModels.NodeEntityModels.NodeEntityType) {
    const nodeClone = structuredClone(node);
    
    // Change Node Visibility
    nodeClone.hidden = !nodeClone.hidden;
    
    this.nodeSharedFacade.requestUpdateNode(nodeClone, 'history.node_update_visibility').subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }

  #toggleRestriction(node: FlatModels.NodeEntityModels.NodeEntityType) {
    const nodeClone = structuredClone(node);
    
    // Change Node Restriction
    nodeClone.restricted = !nodeClone.restricted;
    
    this.nodeSharedFacade.requestUpdateNode(nodeClone, 'history.node_update_restriction').subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }

  #editShares(node: FlatModels.NodeEntityModels.NodeEntityType,
              page: FlatModels.PageEntityModels.PageEntityType) {
    this.dialogService.open(PermissionDialogComponent, {
      header: page.title + ' ' + this.translate.instant('global.user_shares'),
      data: {
        node
      },
      dismissableMask: true,
      width: '90%',
      height: '90%'
    });
  }

  //</editor-fold>
}